import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TenantBills from './TenantBills';
import TenantBillsHistory from './TenantBillsHistory';
import TenantVacatePage from './TenantVacatePage';
import TenantComplaints from './TenantComplaints';
import TenantOccupants from './TenantOccupants';
import AdminAddBill from './AdminAddBill';
import AdminDashboard from './AdminDashboard';
import AdminBulkBills from './AdminBulkBills';
import AdminUsers from './AdminUsers';
import AdminViewBills from './AdminViewBills';
import AdminRoomNotes from './AdminRoomNotes';
import AdminExpenses from './AdminExpenses';
import AdminPaidBillsReport from './AdminPaidBillsReport';
import AdminComplaints from './AdminComplaints';
import AdminTenantDocuments from './AdminTenantDocuments';
import AdminVacateRequests from './AdminVacateRequests';
import AdminArchivedTenants from './AdminArchivedTenants';
import Home from './Home';
import Privacy from './Privacy';
import Login from './Login';
import Registration from './Registration';
import ForgotReset from './ForgotReset';
import Terms from './Terms';
import RefundPolicy from './RefundPolicy';
import ChatAssistant from './ChatAssistant';
import AppShell from './AppShell';
import { API_BASE, API_PREFIX } from './apiClient';
import './i18n';
import './theme.css';

// App version (exposed via environment variable REACT_APP_VERSION)
const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';
// Commit actually baked into this build -- set from Render's own
// RENDER_GIT_COMMIT build arg, so it's always accurate with no manual bump.
const FRONTEND_COMMIT = (process.env.REACT_APP_GIT_COMMIT || 'local-dev').slice(0, 7);

// Keep inactivity limit outside component so it's stable and excluded from hook dependency warnings
const INACTIVITY_LIMIT = 5 * 60 * 1000; // 5 minutes
// After the limit, ask "still there?" and only log out if nobody answers in this long.
const IDLE_GRACE_SECONDS = 60;

function App() {
  const { t } = useTranslation();
  // Read synchronously, not in an effect: an effect runs AFTER the first render, so for
  // one tick `user` would be null and the app would show the logged-out routes. On a
  // direct deep link (e.g. /vacate) that tick's wildcard route rewrites the address to
  // "/" before the session loads, silently discarding the intended page once logged in.
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return null;
    try {
      const parsed = JSON.parse(storedUser);
      if (parsed.lastActivity && Date.now() - parsed.lastActivity > INACTIVITY_LIMIT) {
        localStorage.removeItem('user');
        return null;
      }
      return parsed;
    } catch {
      localStorage.removeItem('user');
      return null;
    }
  });
  const [backendCommit, setBackendCommit] = useState(null);
  const [idlePrompt, setIdlePrompt] = useState(false);
  const [idleLeft, setIdleLeft] = useState(IDLE_GRACE_SECONDS);
  const timerRef = useRef(null);
  const promptOpenRef = useRef(false);
  const resetTimerRef = useRef(null);

  // Fetch the backend's own deployed commit so the footer can show whether
  // frontend and backend are both on their latest deploy at a glance.
  useEffect(() => {
    fetch(`${API_BASE}${API_PREFIX}/version`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setBackendCommit(data.commit))
      .catch(() => {});
  }, []);

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    clearTimeout(timerRef.current);
    promptOpenRef.current = false;
    setIdlePrompt(false);
  };

  const footer = (
    <>
      <Link to="/terms">Terms & Conditions</Link>
      <Link to="/refund-policy">Cancellation & Refund Policy</Link>
      <Link to="/privacy">Privacy Policy</Link>
      <span style={{ opacity: 0.8 }}>Version {APP_VERSION} ({FRONTEND_COMMIT})</span>
      {backendCommit && <span style={{ opacity: 0.8, marginLeft: 8 }}>· API {backendCommit}</span>}
    </>
  );

  const loginPage = <Login setUser={(u) => { setUser(u); localStorage.setItem('user', JSON.stringify(u)); }} />;

  const defaultRoute = user?.role === 'ADMIN' ? '/admin/dashboard' : '/';

  const resumeSession = () => {
    promptOpenRef.current = false;
    setIdlePrompt(false);
    if (resetTimerRef.current) resetTimerRef.current();
  };

  // Count down the grace period while the prompt is showing; nobody answered -> log out.
  useEffect(() => {
    if (!idlePrompt) return undefined;
    const id = setInterval(() => setIdleLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [idlePrompt]);

  useEffect(() => {
    if (idlePrompt && idleLeft <= 0) {
      try { sessionStorage.setItem('loginNotice', t('idle.notice')); } catch { /* ignore */ }
      handleLogout();
    }
  }, [idlePrompt, idleLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inactivity logic: after INACTIVITY_LIMIT of no activity, ask before logging out
  useEffect(() => {
    if (!user) return;

    const showIdlePrompt = () => {
      promptOpenRef.current = true;
      setIdleLeft(IDLE_GRACE_SECONDS);
      setIdlePrompt(true);
    };

    const resetTimer = () => {
      // While the prompt is up, stray mouse movement must not silently dismiss it --
      // only the Resume button counts.
      if (promptOpenRef.current) return;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(showIdlePrompt, INACTIVITY_LIMIT);
      // Persist latest activity timestamp so a full page reload / dev server restart can still evaluate expiry
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.lastActivity = Date.now();
          localStorage.setItem('user', JSON.stringify(parsed));
        }
      } catch { /* ignore */ }
    };

    // List of events that indicate activity
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    events.forEach(event =>
      window.addEventListener(event, resetTimer)
    );

    resetTimerRef.current = resetTimer;
    resetTimer(); // Start timer on mount

    return () => {
      resetTimerRef.current = null;
      clearTimeout(timerRef.current);
      events.forEach(event =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [user]);

  return (
    <Router>
      {!user ? (
        <Routes>
          {/* The link opens straight onto login; the owner/contact page lives at /about. */}
          <Route path="/" element={loginPage} />
          <Route path="/about" element={<Home />} />
          <Route path="/register" element={<Registration />} />
          <Route path="/forgot" element={<ForgotReset />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/login" element={loginPage} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <AppShell user={user} onLogout={handleLogout} footer={footer}>
          <Routes>
            {/* Also where the installed app opens to (manifest start_url) -- an admin must never
                land on the tenant's own bills/deposit page, so send them to their dashboard instead. */}
            <Route path="/" element={user.role === 'ADMIN' ? <Navigate to="/admin/dashboard" replace /> : <TenantBills username={user.username} fullName={user.fullName} />} />
            {/* Same reasoning as "/": these are a tenant's own pages, so an admin typing/bookmarking
                one is sent to the admin page that covers the same thing instead. */}
            <Route path="/history" element={user.role === 'ADMIN' ? <Navigate to="/admin/dashboard" replace /> : <TenantBillsHistory username={user.username} />} />
            <Route path="/vacate" element={user.role === 'ADMIN' ? <Navigate to="/admin/vacate" replace /> : <TenantVacatePage />} />
            <Route path="/complaints" element={user.role === 'ADMIN' ? <Navigate to="/admin/complaints" replace /> : <TenantComplaints username={user.username} />} />
            <Route path="/occupants" element={user.role === 'ADMIN' ? <Navigate to="/admin/tenant-docs" replace /> : <TenantOccupants username={user.username} />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            {user.role === "ADMIN" && (
              <>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/add-bill" element={<AdminAddBill />} />
                <Route path="/admin/bulk-bills" element={<AdminBulkBills />} />
                <Route path="/admin/view-bills" element={<AdminViewBills />} />
                <Route path="/admin/notes" element={<AdminRoomNotes />} />
                <Route path="/admin/expenses" element={<AdminExpenses />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/paid-report" element={<AdminPaidBillsReport />} />
                <Route path="/admin/complaints" element={<AdminComplaints />} />
                {/* Redirect legacy occupants path to new tenant docs */}
                <Route path="/admin/occupants" element={<Navigate to="/admin/tenant-docs" replace />} />
                <Route path="/admin/tenant-docs" element={<AdminTenantDocuments />} />
                <Route path="/admin/vacate" element={<AdminVacateRequests />} />
                <Route path="/admin/archived" element={<AdminArchivedTenants />} />
              </>
            )}
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
          {user.role !== 'ADMIN' && <ChatAssistant username={user.username} onLogout={handleLogout} />}
          {idlePrompt && (
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="idle-title"
              style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}
            >
              <div style={{ background: '#fff', borderRadius: 14, padding: '24px 28px', maxWidth: 380, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.3)', textAlign: 'center' }}>
                <h3 id="idle-title" style={{ margin: '0 0 10px', color: 'var(--ink)' }}>{t('idle.title')}</h3>
                <p style={{ margin: '0 0 18px', color: 'var(--ink-soft)', fontSize: 14, lineHeight: 1.5 }}>
                  {t('idle.body', { seconds: Math.max(idleLeft, 0) })}
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button
                    autoFocus
                    onClick={resumeSession}
                    style={{ background: 'var(--grad-primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 'bold', cursor: 'pointer' }}
                  >{t('idle.resume')}</button>
                  <button
                    onClick={handleLogout}
                    style={{ background: 'var(--line)', color: 'var(--ink-soft)', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 'bold', cursor: 'pointer' }}
                  >{t('idle.logout')}</button>
                </div>
              </div>
            </div>
          )}
        </AppShell>
      )}
    </Router>
  );
}

export default App;